import * as types from "../mutation-types";
import isEqual from "lodash.isequal";

import {
    generateUUID,
    getDateBeforeYears,
    getNonOneTimeStartDate,
    getNonOneTimeEndDate,
    isValidTransactionRecord,
    getPortfolioDate,
    checkContributions,
    checkRebalancing
} from "@/util.js";
import { getCustomTimePeriodDateRange } from "@/common/timePeriod.js";
import cloneDeep from "lodash.clonedeep";

const BenchmarkTypes = [
    {
        id: 1,
        labelId: "benchmark_auto_by_asset_allocation"
    },
    {
        id: 2,
        labelId: "benchmark_auto_by_mstar_category"
    },
    {
        id: 0,
        labelId: "benchmark_standard"
    },
    {
        id: 3,
        labelId: "benchmark_custom"
    }
];

const state = {
    rawPortfolioList: [],
    portfolioList: [],
    editingPortfolioId: ""
};

const moreThanOneYearBeforeReleaseDate = (hypoEndDate, date) => {
    const dateBeforeOneYear = getDateBeforeYears(date, 1);
    return new Date(hypoEndDate) < new Date(dateBeforeOneYear);
};

const hasSameTimePeriod = allSecurities => {
    const { timePeriod, presetTimePeriod } = allSecurities[0];
    return allSecurities.every(security => {
        return (
            security.timePeriod === timePeriod && (security.presetTimePeriod && security.presetTimePeriod.type) === (presetTimePeriod && presetTimePeriod.type)
        );
    });
};

const getters = {
    hasPortfolio(state) {
        return state.portfolioList.length > 0;
    },
    portfoliosLength(state) {
        return state.portfolioList.length;
    },
    hasPortfolioChange(state) {
        return !isEqual(state.portfolioList, state.rawPortfolioList);
    },
    benchmarkTypes() {
        return BenchmarkTypes;
    },
    editingPortfolio(state) {
        const { portfolioList } = state;
        const editingIndex = portfolioList.findIndex(portfolio => state.editingPortfolioId === portfolio.id);
        if (editingIndex !== -1) {
            return {
                ...portfolioList[editingIndex]
            };
        } else {
            return null;
        }
    },
    editingPortfolioTitle(state) {
        const { portfolioList } = state;
        const editingIndex = portfolioList.findIndex(portfolio => state.editingPortfolioId === portfolio.id);
        if (editingIndex !== -1) {
            return `Portfolio ${editingIndex + 1}`;
        } else {
            return "";
        }
    },
    portfolioOlderThanOneYearWarning: (state, getters, rootState, rootGetters) => (portfolioNamePrefix, conjunction) => {
        const releaseDate = rootGetters["chart/assetAllocationReleaseDate"];
        const hypoType = parseInt(rootGetters["hypo/getType"], 10);
        const portfolioList = state.portfolioList
            .map((item, index) => {
                return {
                    ...item,
                    portfolioIndex: index + 1
                };
            })
            .filter(item => {
                return item.securities.some(security => {
                    return moreThanOneYearBeforeReleaseDate(security.endDate, releaseDate);
                });
            });
        if (portfolioList.length === 1 && hypoType === 0) {
            return portfolioNamePrefix;
        } else if (portfolioList.length === 1 && hypoType === 1) {
            return `${portfolioNamePrefix} ${portfolioList[0].portfolioIndex}`;
        } else if (portfolioList.length === 2 && hypoType === 1) {
            return `${portfolioNamePrefix} ${portfolioList[0].portfolioIndex} ${conjunction} ${portfolioNamePrefix} ${portfolioList[1].portfolioIndex}`;
        }
        return "";
    },
    allSecurities(state) {
        const { portfolioList } = state;
        return portfolioList.reduce((prev, portfolio) => {
            return [...prev, ...portfolio.securities];
        }, []);
    },
    globalTimePeriod(state, getters) {
        const allSecurities = getters.allSecurities;
        if (allSecurities.length > 0 && hasSameTimePeriod(allSecurities)) {
            const { timePeriod, presetTimePeriod } = allSecurities[0];
            return presetTimePeriod ? `${timePeriod}-${presetTimePeriod.type}` : timePeriod;
        }
        return -1;
    },
    portfolioCustomTimePeriodDateRanges(state) {
        const portfolioDateRanges = state.portfolioList.map(portfolio => {
            return getCustomTimePeriodDateRange(portfolio.securities) || { startDate: "", endDate: "" };
        });
        const { startDate, endDate } = portfolioDateRanges[0] || { startDate: "", endDate: "" };
        const hasSameStartDate = portfolioDateRanges.every(dateRange => dateRange.startDate !== "" && dateRange.startDate === startDate);
        const hasSameEndDate = portfolioDateRanges.every(dateRange => dateRange.endDate !== "" && dateRange.endDate === endDate);
        return {
            startDate: hasSameStartDate ? startDate : "",
            endDate: hasSameEndDate ? endDate : ""
        };
    },
    customTimePeriodDates(state, getters) {
        const isCustomTimePeriod = getters.globalTimePeriod === 0;
        if (isCustomTimePeriod) {
            return getters.portfolioCustomTimePeriodDateRanges;
        }
        return {
            startDate: "",
            endDate: ""
        };
    }
};

function initPortfolio() {
    return {
        id: "", //unique id
        portfolioId: "",
        name: "",
        currency: "USD",
        allocateBy: 0,
        totalInvestAmount: 0,
        securities: []
    };
}

const mutations = {
    [types.ADD_PORTFOLIO](state, data) {
        state.portfolioList.push(data);
    },
    [types.DELETE_PORTFOLIO](state, id) {
        const index = state.portfolioList.findIndex(p => p.id === id);
        state.portfolioList.splice(index, 1);
    },
    [types.UPDATE_PORTFOLIOLIST](state, data) {
        state.portfolioList = data;
    },
    [types.UPDATE_RAW_PORTFOLIOLIST](state, data) {
        state.rawPortfolioList = data;
    },
    [types.SET_EDITING_PORTFOLIO_ID](state, data) {
        state.editingPortfolioId = data;
    },
    [types.RESET](state) {
        state.portfolioList = [];
    }
};

function addNewPortfolio(commit, rootGetters, index) {
    const preferSettings = rootGetters["preference/getPreferenceSettings"];
    const hypoInput = rootGetters.hypoInput;
    const portfolio = {
        ...initPortfolio(),
        ...preferSettings
    };
    // this property only works with new hypos, is not saved when save the hypo occurs
    // and doesn't work with reopend files. Later we will need to save it. [HYPO-4322]
    if (index === 1) {
        portfolio.synced = false;
        portfolio.firstSynced = false;
    }
    portfolio.id = generateUUID();
    portfolio.portfolioId = portfolio.id;
    portfolio.name = `Untitled`;
    if (!hypoInput.hypoId) {
        commit(`hypo/${types.SET_HYPO_NAME}`, "Untitled", { root: true });
    }
    commit(types.ADD_PORTFOLIO, portfolio);
}

function getSubsequentSettings(originPortfolio, updatedPortfolio) {
    const portfolioDate = getPortfolioDate(updatedPortfolio);
    const subSequenstLength =
        originPortfolio.subSequentSetting && originPortfolio.subSequentSetting.subSequents ? originPortfolio.subSequentSetting.subSequents.length : 0;
    const subSequentsData =
        subSequenstLength > 0
            ? originPortfolio.subSequentSetting.subSequents.map(item => {
                  const isOneTime = item.frequency === 99;
                  return {
                      ...item,
                      startDate: !isOneTime ? getNonOneTimeStartDate(portfolioDate.startDate) : item.startDate,
                      endDate: !isOneTime ? getNonOneTimeEndDate(portfolioDate.endDate) : item.endDate,
                      visible: !isOneTime ? true : isValidTransactionRecord(item.startDate, portfolioDate)
                  };
              })
            : [];
    return {
        ...originPortfolio.subSequentSetting,
        subSequents: subSequentsData
    };
}
function updatePortfolioSecurities(portfolio, feeSetting) {
    return portfolio.securities.map(security => {
        let securityFees = {};
        if (feeSetting.includeAdvisoryFee) {
            securityFees = {
                annualFeeAmountType: 1, //UI only support percentage now.
                annualFeeAmount: feeSetting.annualFeeAmount,
                annualFeeChargeFrequency: feeSetting.annualFeeChargeFrequency
            };
        }
        if (feeSetting.includeTransactionFee) {
            securityFees = {
                ...securityFees,
                frontLoadType: feeSetting.frontLoadType,
                loi: feeSetting.frontLoadType === 4,
                redemptionCharge: feeSetting.redemptionCharge
            };
            if (feeSetting.frontLoadType === 1) {
                // custom $ / Percent
                securityFees = {
                    ...securityFees,
                    frontLoadAmountType: feeSetting.frontLoadAmountType,
                    frontLoadAmount: feeSetting.frontLoadAmount
                };
            }
        }
        return {
            ...security,
            fees: securityFees
        };
    });
}
const actions = {
    addSecuritiesfromPCT({ commit, state }, data) {
        const { index } = data;
        const { updatedPortfolio } = data;
        const updatedList = state.portfolioList.map((portfolio, i) => {
            return i === index ? updatedPortfolio : portfolio;
        });
        commit(types.UPDATE_PORTFOLIOLIST, updatedList);
    },
    setPortfoliosByFlow({ commit, rootGetters }) {
        const type = parseInt(rootGetters["hypo/getType"]);
        commit(types.RESET);
        switch (type) {
            case 0:
                addNewPortfolio(commit, rootGetters, 0);
                break;

            case 1:
                addNewPortfolio(commit, rootGetters, 0);
                addNewPortfolio(commit, rootGetters, 1);
                break;

            case 2:
                addNewPortfolio(commit, rootGetters, 0);
                break;
            default:
                break;
        }
    },
    setNewPortfolioByIndex({ commit, rootGetters }, index) {
        addNewPortfolio(commit, rootGetters, index);
    },
    duplicatePortfolio({ state, commit, getters, rootState }) {
        //copy portfolio
        const newPortfolioId = generateUUID();
        const newList = [state.portfolioList[0], { ...state.portfolioList[0], id: newPortfolioId, portfolioId: newPortfolioId }];
        commit(types.UPDATE_RAW_PORTFOLIOLIST, newList);
        commit(types.UPDATE_PORTFOLIOLIST, newList);
    },
    syncPortfolioAssumptions({ state, commit }) {
        const { portfolioList } = state;
        let updatedPortfolio = cloneDeep(portfolioList[1]);
        const originPortfolio = cloneDeep(portfolioList[0]);

        if (!updatedPortfolio.firstSynced) {
            updatedPortfolio.firstSynced = true;
            originPortfolio.firstSynced = true;
        }
        updatedPortfolio.reinvestSetting = originPortfolio.reinvestSetting ? { ...originPortfolio.reinvestSetting } : null;
        updatedPortfolio.taxSetting = originPortfolio.taxSetting ? { ...originPortfolio.taxSetting } : null;
        updatedPortfolio.benchmark = originPortfolio.benchmark ? { ...originPortfolio.benchmark } : null;

        updatedPortfolio.feeSetting = { ...originPortfolio.feeSetting };
        const feeSetting = cloneDeep(updatedPortfolio.feeSetting);
        if (feeSetting && (feeSetting.includeAdvisoryFee || feeSetting.includeTransactionFee)) {
            updatedPortfolio.securities = updatePortfolioSecurities(updatedPortfolio, feeSetting);
        }
        updatedPortfolio.rebalance = originPortfolio.rebalance && checkRebalancing(originPortfolio) ? { ...originPortfolio.rebalance } : null;

        updatedPortfolio.subSequentSetting = checkContributions(originPortfolio, updatedPortfolio)
            ? getSubsequentSettings(originPortfolio, updatedPortfolio)
            : null;

        updatedPortfolio = Object.keys(updatedPortfolio).reduce((acc, key) => {
            if (updatedPortfolio[key] !== null) {
                acc[key] = updatedPortfolio[key];
            }
            return acc;
        }, {});
        updatedPortfolio.synced = true;
        const newList = [originPortfolio, updatedPortfolio];
        commit(types.UPDATE_PORTFOLIOLIST, newList);
        commit(types.UPDATE_RAW_PORTFOLIOLIST, newList);
    },
    renamePortfolio({ state, commit, getters, rootState }, payload) {
        const portfolioIndex = parseInt(payload.portfolioNumber);
        const updatedPortfolio = { ...state.portfolioList[portfolioIndex] };
        updatedPortfolio.name = payload.newName;
        const updatedList = state.portfolioList.map((portfolio, index) => {
            return index === portfolioIndex ? updatedPortfolio : portfolio;
        });
        commit(types.UPDATE_PORTFOLIOLIST, updatedList);
    },
    deletePortfolio({ state, getters, rootState, commit }, portfolioId) {
        function deleteData(rawData) {
            return rawData.filter(item => item.id !== portfolioId);
        }

        //delete portfolio
        const newList = deleteData(state.portfolioList);
        let newChartData = null;
        if (newList.length > 0) {
            //delete chart data
            const rawChartData = rootState.chart.chartData;

            const newPerformanceData = {};
            Object.keys(rawChartData.performanceData).forEach(key => {
                newPerformanceData[key] = deleteData(rawChartData.performanceData[key]);
            });

            const rawAssetDatas = rawChartData.assetAllocation.assetDatas;
            const newAssetDatas = {};
            Object.keys(rawAssetDatas).forEach(key => {
                newAssetDatas[key] = deleteData(rawAssetDatas[key]);
            });

            newChartData = {
                assetAllocation: {
                    assetGroups: rawChartData.assetAllocation.assetGroups,
                    assetDatas: newAssetDatas,
                    releaseDate: rawChartData.assetAllocation.releaseDate
                },
                growth: deleteData(rawChartData.growth),
                investments: deleteData(rawChartData.investments),
                performanceData: newPerformanceData,
                weights: deleteData(rawChartData.weights)
            };
        }

        if (!getters.hasPortfolioChange) {
            commit(types.UPDATE_RAW_PORTFOLIOLIST, newList);
        }
        commit(types.UPDATE_PORTFOLIOLIST, newList);
        commit(`chart/${types.SET_CHART_DATA}`, newChartData, { root: true });
    }
};

export default { state, getters, mutations, actions };
